import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from "@/components/ui/button";
import { Contrast, Speech, Keyboard, X, Type, Moon, Sun, Eye, EyeOff } from 'lucide-react';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  structuredData?: Record<string, any>;
  keywords?: string;
}

interface AccessibilityProviderProps {
  children: React.ReactNode;
  seo?: SEOProps;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children, seo }) => {
  const [isAccessibilityOpen, setIsAccessibilityOpen] = useState(false);
  const [fontSize, setFontSize] = useState(0);
  const [highContrast, setHighContrast] = useState(false);
  const [grayscale, setGrayscale] = useState(false);
  const [invertColors, setInvertColors] = useState(false);
  const [emphasizeLinks, setEmphasizeLinks] = useState(false);
  const [readableFont, setReadableFont] = useState(false);
  
  useEffect(() => {
    // Apply accessibility settings to the document
    const htmlElement = document.documentElement;
    
    // Font size adjustment
    if (fontSize !== 0) {
      const fontSizePercentage = 100 + fontSize * 10;
      htmlElement.style.fontSize = `${fontSizePercentage}%`;
    } else {
      htmlElement.style.fontSize = '';
    }
    
    // High contrast
    if (highContrast) {
      htmlElement.classList.add('high-contrast');
    } else {
      htmlElement.classList.remove('high-contrast');
    }
    
    // Grayscale
    if (grayscale) {
      htmlElement.classList.add('grayscale');
    } else {
      htmlElement.classList.remove('grayscale');
    }
    
    // Invert colors
    if (invertColors) {
      htmlElement.classList.add('inverted');
    } else {
      htmlElement.classList.remove('inverted');
    }
    
    // Emphasize links
    if (emphasizeLinks) {
      htmlElement.classList.add('emphasize-links');
    } else {
      htmlElement.classList.remove('emphasize-links');
    }
    
    // Readable font
    if (readableFont) {
      htmlElement.classList.add('readable-font');
    } else {
      htmlElement.classList.remove('readable-font');
    }
    
    // Save settings to localStorage
    const settings = { 
      fontSize, 
      highContrast, 
      grayscale,
      invertColors,
      emphasizeLinks,
      readableFont
    };
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    
  }, [fontSize, highContrast, grayscale, invertColors, emphasizeLinks, readableFont]);
  
  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('accessibility-settings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setFontSize(settings.fontSize || 0);
        setHighContrast(settings.highContrast || false);
        setGrayscale(settings.grayscale || false);
        setInvertColors(settings.invertColors || false);
        setEmphasizeLinks(settings.emphasizeLinks || false);
        setReadableFont(settings.readableFont || false);
      }
    } catch (error) {
      console.error('Error loading accessibility settings:', error);
    }
  }, []);
  
  const resetSettings = () => {
    setFontSize(0);
    setHighContrast(false);
    setGrayscale(false);
    setInvertColors(false);
    setEmphasizeLinks(false);
    setReadableFont(false);
    localStorage.removeItem('accessibility-settings');
  };

  return (
    <>
      {/* SEO Tags with React Helmet */}
      {seo && (
        <Helmet>
          <title>{seo.title}</title>
          <meta name="description" content={seo.description} />
          {seo.keywords && <meta name="keywords" content={seo.keywords} />}
          
          {/* Canonical URL */}
          {seo.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}
          
          {/* Open Graph Meta Tags */}
          <meta property="og:title" content={seo.title} />
          <meta property="og:description" content={seo.description} />
          <meta property="og:type" content="website" />
          {seo.canonicalUrl && <meta property="og:url" content={seo.canonicalUrl} />}
          {seo.ogImage && <meta property="og:image" content={seo.ogImage} />}
          
          {/* Twitter Card Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seo.title} />
          <meta name="twitter:description" content={seo.description} />
          {seo.ogImage && <meta name="twitter:image" content={seo.ogImage} />}
          
          {/* Structured Data */}
          {seo.structuredData && (
            <script type="application/ld+json">
              {JSON.stringify(seo.structuredData)}
            </script>
          )}
        </Helmet>
      )}
      
      {/* Accessibility Button */}
      <button 
        onClick={() => setIsAccessibilityOpen(true)}
        className="fixed left-4 bottom-20 z-40 p-3 bg-tech-blue text-white rounded-full shadow-lg hover:bg-tech-blue/90 focus:outline-none focus:ring-2 focus:ring-tech-blue focus:ring-offset-2"
        aria-label="פתח תפריט נגישות"
        title="נגישות"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" />
        </svg>
      </button>
      
      {/* Accessibility Panel */}
      {isAccessibilityOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black bg-opacity-50">
          <div 
            className="fixed left-0 bottom-0 top-0 w-full max-w-md bg-white shadow-xl p-6 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="accessibility-title"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 id="accessibility-title" className="text-2xl font-bold">הגדרות נגישות</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsAccessibilityOpen(false)}
                aria-label="סגור תפריט נגישות"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            
            <div className="space-y-6">
              {/* גודל טקסט */}
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <Type className="ml-2 h-5 w-5" />
                  גודל טקסט
                </h3>
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setFontSize(Math.max(fontSize - 1, -2))}
                    disabled={fontSize <= -2}
                    aria-label="הקטן טקסט"
                  >
                    A-
                  </Button>
                  <span className="mx-4 min-w-16 text-center">
                    {fontSize === 0 ? 'רגיל' : fontSize > 0 ? `${fontSize * 10}%+` : `${Math.abs(fontSize) * 10}%-`}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setFontSize(Math.min(fontSize + 1, 5))}
                    disabled={fontSize >= 5}
                    aria-label="הגדל טקסט"
                  >
                    A+
                  </Button>
                </div>
              </div>
              
              {/* ניגודיות */}
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <Contrast className="ml-2 h-5 w-5" />
                  ניגודיות
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant={highContrast ? "default" : "outline"}
                    onClick={() => setHighContrast(!highContrast)}
                    aria-pressed={highContrast}
                    className="flex items-center justify-center"
                  >
                    <Eye className="ml-2 h-4 w-4" />
                    ניגודיות גבוהה
                  </Button>
                  <Button
                    variant={invertColors ? "default" : "outline"}
                    onClick={() => setInvertColors(!invertColors)}
                    aria-pressed={invertColors}
                    className="flex items-center justify-center"
                  >
                    <Moon className="ml-2 h-4 w-4" />
                    היפוך צבעים
                  </Button>
                  <Button
                    variant={grayscale ? "default" : "outline"}
                    onClick={() => setGrayscale(!grayscale)}
                    aria-pressed={grayscale}
                    className="flex items-center justify-center"
                  >
                    <EyeOff className="ml-2 h-4 w-4" />
                    גווני אפור
                  </Button>
                </div>
              </div>
              
              {/* תוכן */}
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <Type className="ml-2 h-5 w-5" />
                  תוכן
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant={emphasizeLinks ? "default" : "outline"}
                    onClick={() => setEmphasizeLinks(!emphasizeLinks)}
                    aria-pressed={emphasizeLinks}
                    className="flex items-center justify-center"
                  >
                    <Speech className="ml-2 h-4 w-4" />
                    הדגשת קישורים
                  </Button>
                  <Button
                    variant={readableFont ? "default" : "outline"}
                    onClick={() => setReadableFont(!readableFont)}
                    aria-pressed={readableFont}
                    className="flex items-center justify-center"
                  >
                    <Keyboard className="ml-2 h-4 w-4" />
                    גופן קריא
                  </Button>
                </div>
              </div>
              
              {/* איפוס */}
              <div className="pt-4">
                <Button
                  variant="outline"
                  className="w-full border-destructive text-destructive"
                  onClick={resetSettings}
                >
                  איפוס הגדרות נגישות
                </Button>
              </div>
              
              {/* הצהרת נגישות */}
              <div className="pt-4">
                <Button
                  variant="link"
                  className="w-full"
                  onClick={() => {
                    setIsAccessibilityOpen(false);
                    window.location.href = '/accessibility';
                  }}
                >
                  הצהרת נגישות
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {children}
    </>
  );
};

export default AccessibilityProvider;